import React from 'react';
import { useStore } from 'effector-react';
import { toFlag } from 'pano-utils';
import { type HSNonPolygonIcon, type Hotspot, SceneType } from '@types';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import { $curScene } from '@stores/dataStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import FeatureToggle from '@components/FeatureToggle';
import { getHotspotTitleId } from '@modules/hotspot';

function StickSetting() {
  const { hotspot, isBatch, batchHotspots } = useStore($editingHotspot)!;
  const scene = useStore($curScene);
  if (!scene) return null;
  const { type } = scene;
  const icon = hotspot.icon as HSNonPolygonIcon;

  const { isZoomOn, isStickOn }: { isStickOn: boolean; isZoomOn: boolean } =
    (() => {
      // zoom开启时, 数据中stick是关闭, 但显示开启
      if (icon.zoom) return { isStickOn: true, isZoomOn: true };
      // zoom关闭时, stick根据数据显示
      return { isStickOn: !!icon.stick, isZoomOn: false };
    })();

  const updateHotspots = (
    hotspotList: Hotspot[],
    { distorted, zoom }: { distorted: boolean; zoom: boolean }
  ) => {
    hotspotList.forEach((item) => {
      MainPlayer.get()?.updateHotspot({ id: item.id, distorted, zoom });
      const titleId = getHotspotTitleId(item.id, item.icon.type);
      if (titleId) {
        MainPlayer.get()?.updateHotspot({ id: titleId, distorted, zoom });
      }
    });
  };

  /**
   * 开关stick时, 可以把zoom都置为false
   */
  const handleToggleStick = (bool: boolean) => {
    $editingHotspotApi.update((draft) => {
      const iconData = draft.icon as HSNonPolygonIcon;
      iconData.stick = toFlag(bool);
      iconData.zoom = false;
    });
    const list = isBatch ? batchHotspots ?? [] : [hotspot];
    updateHotspots(list, { distorted: bool, zoom: false });
  };

  /**
   * 打开zoom时, 需要把实际地stick设置为false
   * 关闭zoom时, 则打开
   */
  const handleToggleZoom = (bool: boolean) => {
    $editingHotspotApi.update((draft) => {
      const iconData = draft.icon as HSNonPolygonIcon;
      iconData.stick = toFlag(!bool);
      iconData.zoom = bool;
    });
    const list = isBatch ? batchHotspots ?? [] : [hotspot];
    updateHotspots(list, { distorted: !bool, zoom: bool });
  };

  if (type !== SceneType.pano && type !== SceneType.matrix) return null;

  return (
    <>
      <FeatureToggle
        className="mt-20"
        title="跟随画面同步缩放"
        isOn={isStickOn}
        onToggle={handleToggleStick}
      />
      {isStickOn && (
        <FeatureToggle
          className="mt-20"
          title="固定水平垂直显示"
          isOn={isZoomOn}
          onToggle={handleToggleZoom}
        />
      )}
    </>
  );
}

export default StickSetting;
